import {
  IFRAME_CLASS,
  MODAL_BACKDROP_CLASS,
  MODAL_CLOSE_BUTTON_CLASS,
  MODAL_DIALOG_CLASS,
  SNIPPET_SELECTOR,
} from '@/constants'

export function addWidgetStyles() {
  const style = document.createElement('style')

  // `height: 0` is the loading state - the spinner element added by
  // showIframeLoader (rendered above the iframe in the snippet) provides the
  // visual feedback while iframe-resizer's handshake completes. The `min-height`
  // floor is a safety net: if iframe-resizer fails to initialize for any reason
  // (e.g. its postMessage handshake is rejected, or the resizer fails to load),
  // the widget stays visible at a usable size instead of collapsing to 0. Once
  // iframe-resizer succeeds, its `initCallback` clears this floor (inline
  // `min-height: 0`) so widgets with <600px content can shrink to fit.
  // The duplicate min-width is intentional: `min-width: 100%` is the classic
  // iframe-resizer responsive-width setup, but it resolves to 0 when the host
  // page gives the snippet a zero-width container (shrink-to-fit flex/grid
  // items, inline parents), leaving a 1px-wide vertical sliver. The max()
  // declaration floors the width so the widget stays visible even then;
  // browsers without max() support fall back to the plain 100% line.
  // The snippet div sits in whatever container the host page provides, and
  // some hosts make it a flex item (seen live: site CSS styling the V1
  // <healcode-widget> element as display:flex). An auto-basis flex item
  // shrinks to fit, the iframe's percentage min-width can't resolve against
  // it, and the widget pins at the 250px hard floor even with a full-width
  // flex container around it. `width: 100%` fills any container percentages
  // can resolve against, and the flex shorthand - ignored entirely outside
  // flex layouts - lets the snippet take the flex container's free space
  // instead of shrinking to fit.
  // The bw-widget-modal rules style the timer-triggered overlay dialog
  // (widgetModal.ts). The z-index sits just under the int32 max so the
  // overlay beats host chrome without colliding with our own future layers;
  // at phone widths (<= 600px) the same dialog becomes a full-screen
  // takeover via the media query - no separate code path.
  // Every modal declaration is !important. Unlike the inline widget - which
  // hosts may legitimately restyle within bounds - the overlay is ours end
  // to end, and a host theme resetting broad selectors with !important
  // (`div { ... !important }` resets are common in site themes) can leave it
  // unusable with no way to recover. Scoped to the modal classes only: the snippet and
  // iframe rules above keep their existing cascade weight.
  style.innerHTML = `
      ${SNIPPET_SELECTOR}{
        width: 100%;
        flex: 1 1 auto;
      }
      .${IFRAME_CLASS}{
        width: 1px;
        min-width: 100%;
        min-width: max(100%, 250px);
        height: 0;
        min-height: 600px;
        border: 0;
        border-radius: 16px;
      }
      .${MODAL_BACKDROP_CLASS}{
        position: fixed !important;
        inset: 0 !important;
        background: rgba(0, 0, 0, 0.5) !important;
        z-index: 2147483000 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      .${MODAL_DIALOG_CLASS}{
        position: relative !important;
        width: min(480px, 100vw - 32px) !important;
        max-height: 90dvh !important;
        border-radius: 16px !important;
        background: transparent !important;
        overflow: auto !important;
      }
      .${MODAL_CLOSE_BUTTON_CLASS}{
        position: absolute !important;
        top: 8px !important;
        right: 8px !important;
        z-index: 1 !important;
        width: 32px !important;
        height: 32px !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 16px !important;
        background: #ffffff !important;
        color: #000000 !important;
        font-size: 24px !important;
        line-height: 1 !important;
        cursor: pointer !important;
      }
      @media (max-width: 600px) {
        .${MODAL_DIALOG_CLASS}{
          width: 100vw !important;
          height: 100dvh !important;
          max-height: 100dvh !important;
          border-radius: 0 !important;
        }
      }
  `
  document.head.appendChild(style)
}
